/**
 * Input Sanitization Utilities
 * Prevents XSS attacks by sanitizing user inputs before storing in Firebase
 */

/**
 * Removes HTML tags and dangerous characters from string input
 * @param {string} input - Raw user input
 * @returns {string} Sanitized string
 */
export const sanitizeString = (input) => {
  if (typeof input !== 'string') return '';

  return input
    .trim()
    // Remove HTML tags
    .replace(/<[^>]*>/g, '')
    // Remove script tags and content
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    // Remove event handlers
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
    // Remove javascript: protocol
    .replace(/javascript:/gi, '')
    // Limit length to prevent DoS
    .substring(0, 1000);
};

/**
 * Sanitizes phone number input
 * @param {string} phone - Raw phone input
 * @returns {string} Sanitized phone number (digits only)
 */
export const sanitizePhone = (phone) => {
  if (typeof phone !== 'string') return '';

  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');

  // Limit to reasonable phone number length (max 15 digits for international)
  return cleaned.substring(0, 15);
};

/**
 * Sanitizes email input
 * @param {string} email - Raw email input
 * @returns {string} Sanitized email
 */
export const sanitizeEmail = (email) => {
  if (typeof email !== 'string') return '';

  return email
    .trim()
    .toLowerCase()
    // Remove dangerous characters but keep valid email chars
    .replace(/[<>()[\]\\,;:\s@"]/g, (char) => char === '@' || char === '.' || char === '-' || char === '_' ? char : '')
    .substring(0, 254); // RFC 5321 max length
};

/**
 * Sanitizes address input
 * @param {string} address - Raw address input
 * @returns {string} Sanitized address
 */
export const sanitizeAddress = (address) => {
  if (typeof address !== 'string') return '';

  return address
    .trim()
    // Remove HTML tags
    .replace(/<[^>]*>/g, '')
    // Keep alphanumeric, spaces, commas, periods, hyphens
    .replace(/[^\w\s,.\-#/]/g, '')
    .substring(0, 500);
};

/**
 * Sanitizes name input (person or business)
 * @param {string} name - Raw name input
 * @returns {string} Sanitized name
 */
export const sanitizeName = (name) => {
  if (typeof name !== 'string') return '';

  return name
    .trim()
    // Remove HTML tags
    .replace(/<[^>]*>/g, '')
    // Keep only letters, spaces, apostrophes, hyphens, and dots
    .replace(/[^a-zA-Z\s'\-.]/g, '')
    .substring(0, 100);
};

/**
 * Sanitizes general text input (notes, instructions, etc.)
 * @param {string} text - Raw text input
 * @returns {string} Sanitized text
 */
export const sanitizeText = (text) => {
  if (typeof text !== 'string') return '';

  return text
    .trim()
    // Remove HTML tags
    .replace(/<[^>]*>/g, '')
    // Remove script tags
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    // Remove event handlers
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
    .substring(0, 2000);
};

/**
 * Validates and sanitizes Indian phone number
 * @param {string} phone - Phone number to validate
 * @returns {Object} { isValid: boolean, sanitized: string, error: string }
 */
export const validateIndianPhone = (phone) => {
  const sanitized = sanitizePhone(phone);

  // Indian phone numbers: 10 digits, starting with 6-9
  const indianPhoneRegex = /^[6-9]\d{9}$/;

  if (!sanitized) {
    return { isValid: false, sanitized: '', error: 'Phone number is required' };
  }

  if (!indianPhoneRegex.test(sanitized)) {
    return {
      isValid: false,
      sanitized,
      error: 'Invalid Indian phone number. Must be 10 digits starting with 6-9'
    };
  }

  return { isValid: true, sanitized, error: '' };
};

/**
 * Sanitizes customer data object
 * @param {Object} customer - Raw customer data
 * @returns {Object} Sanitized customer data
 */
export const sanitizeCustomerData = (customer) => {
  return {
    name: sanitizeName(customer.name || ''),
    phone: sanitizePhone(customer.phone || ''),
    email: customer.email ? sanitizeEmail(customer.email) : '',
    address: sanitizeAddress(customer.address || ''),
    location: sanitizeAddress(customer.location || ''),
    building: sanitizeString(customer.building || ''),
    // Preserve other safe fields
    orderType: ['delivery', 'takeaway'].includes(customer.orderType) ? customer.orderType : 'delivery',
    latitude: typeof customer.latitude === 'number' ? customer.latitude : null,
    longitude: typeof customer.longitude === 'number' ? customer.longitude : null
  };
};

/**
 * Sanitizes order data object
 * @param {Object} order - Raw order data
 * @returns {Object} Sanitized order data
 */
export const sanitizeOrderData = (order) => {
  return {
    ...order,
    customer: sanitizeCustomerData(order.customer || {}),
    specialInstructions: sanitizeText(order.specialInstructions || ''),
    couponCode: order.couponCode ? sanitizeString(order.couponCode).toUpperCase() : ''
  };
};
